import type { ImageModel } from '@rocco/types/image-model';
import type { SearchResultItem } from '~/modules/search';

import type { BasicThumbnail } from '~/utils/get-tile-thumbnail-for-professional';
import { isBasicThumbnail } from '~/utils/get-tile-thumbnail-for-professional';

type projectWithThumbnail = BasicThumbnail | SearchResultItem;

const isSearchResultItem = (p: projectWithThumbnail): p is SearchResultItem => {
    return (p as SearchResultItem).PType !== undefined;
};

// we have many place need to maintain get the tile thumbnail from a project
// so we create this backward-compatible function to use in all places
// doc: https://archipro.atlassian.net/wiki/spaces/AD/pages/1788313607
export const getProjectTileThumbnail = (
    project: projectWithThumbnail,
    isMobile?: boolean
): string => {
    if (isBasicThumbnail(project)) {
        const thumbnail = isMobile
            ? project.Thumbnail?.mobile
            : project.Thumbnail?.desktop;
        if (thumbnail) {
            return thumbnail;
        }
    }

    // check if project is a SearchResultItem
    if (isSearchResultItem(project)) {
        const thumbs = project.LibraryThumbs;
        const directoryTiles =
            project.DirectoryTiles?.filter((imageTile) => imageTile.Thumb) ??
            [];

        const preferredThumb = directoryTiles?.[0]?.Thumb;

        return (
            preferredThumb ?? thumbs?.Thumb_category ?? thumbs?.Thumb_lg ?? ''
        );
    }

    return '';
};

/**
 * Extracts an array of ImageModel objects from the DirectoryTiles property of a SearchResultItem.
 * Filters out any tiles without a valid Thumb property.
 * This utility is used to provide consistent image data for project tiles across the application.
 *
 * @param item - The SearchResultItem containing DirectoryTiles to extract images from.
 * @returns An array of ImageModel objects for use in project tiles.
 */
export const getDirectoryTileImages = (
    item: SearchResultItem
): ImageModel[] => {
    return (
        item.DirectoryTiles?.filter((imageTile, idx, arr) => {
            if (!imageTile.Thumb) return false;
            // Remove duplicate images by Thumb
            return arr.findIndex((t) => t.Thumb === imageTile.Thumb) === idx;
        }).map((imageTile) => ({
            src: imageTile.Thumb as string,
            alt: item.Title ?? '',
            multiplied: imageTile.IsMultiplied,
        })) || []
    );
};
